import React, { useState, useEffect, useCallback } from 'react';
import { Building2, Search, Eye, CheckCircle, XCircle, X } from 'lucide-react';
import StatusBadge from '../common/StatusBadge';
import EmptyState from '../common/EmptyState';
import { AdminCompanyAPI, APIUtils } from '../../services/api';

const CompanyList = ({ onViewCompany }) => {
  const [companies, setCompanies] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [actionNotes, setActionNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchCompanies = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (search) params.search = search;

      const response = await AdminCompanyAPI.getAll(params);
      if (response.data.success) {
        setCompanies(response.data.data.companies);
        setPagination(response.data.data.pagination);
      }
    } catch (error) {
      console.error('Fetch companies error:', APIUtils.handleError(error));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const handleStatusUpdate = async (companyId, status) => {
    setActionLoading(true);
    try {
      await AdminCompanyAPI.updateStatus(companyId, status, actionNotes);
      setSelectedCompany(null);
      setActionNotes('');
      fetchCompanies();
    } catch (error) {
      alert(APIUtils.handleError(error));
    } finally {
      setActionLoading(false);
    }
  };

  const statusTabs = ['all', 'pending', 'approved', 'rejected', 'suspended'];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Companies</h1>
        <p className="text-gray-500 mt-1">Manage company registrations and approvals</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="p-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex space-x-1">
            {statusTabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setStatusFilter(tab)}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg capitalize transition-colors ${
                  statusFilter === tab
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              placeholder="Search companies..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
          </div>
        ) : companies.length === 0 ? (
          <EmptyState title="No companies found" message="No companies match the current filters." icon={Building2} />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Registered</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {companies.map((company) => (
                    <tr key={company._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium" style={{ backgroundColor: company.logoColor || '#059669' }}>
                            {company.logoInitials || company.companyName?.substring(0, 2).toUpperCase()}
                          </div>
                          <button onClick={() => onViewCompany(company._id)} className="ml-3 font-medium text-emerald-600 hover:text-emerald-800 hover:underline text-left">{company.companyName}</button>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">{company.email}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{company.phoneNumber}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {new Date(company.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        <StatusBadge status={company.registrationStatus} />
                      </td>
                      <td className="px-6 py-4">
                        {/* Mobile-friendly action buttons: 44px+ tap targets
                            with visible labels; wrap on narrow screens. */}
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            onClick={() => setSelectedCompany(company)}
                            className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] text-sm font-medium text-emerald-700 bg-emerald-50 rounded-lg hover:bg-emerald-100"
                            title="View details"
                          >
                            <Eye size={18} />
                            <span>View</span>
                          </button>
                          {company.registrationStatus === 'pending' && (
                            <>
                              <button
                                onClick={() => {
                                  if (window.confirm(`Approve ${company.companyName}?`)) {
                                    handleStatusUpdate(company._id, 'approved');
                                  }
                                }}
                                disabled={actionLoading}
                                className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] text-sm font-medium text-green-700 bg-green-100 rounded-lg hover:bg-green-200 disabled:opacity-50"
                                title="Approve"
                              >
                                <CheckCircle size={18} />
                                <span>Approve</span>
                              </button>
                              <button
                                onClick={() => {
                                  if (window.confirm(`Reject ${company.companyName}?`)) {
                                    handleStatusUpdate(company._id, 'rejected');
                                  }
                                }}
                                disabled={actionLoading}
                                className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] text-sm font-medium text-red-700 bg-red-100 rounded-lg hover:bg-red-200 disabled:opacity-50"
                                title="Reject"
                              >
                                <XCircle size={18} />
                                <span>Reject</span>
                              </button>
                            </>
                          )}
                          {company.registrationStatus === 'rejected' && (
                            <button
                              onClick={() => {
                                if (window.confirm(`Approve ${company.companyName}?`)) {
                                  handleStatusUpdate(company._id, 'approved');
                                }
                              }}
                              disabled={actionLoading}
                              className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] text-sm font-medium text-green-700 bg-green-100 rounded-lg hover:bg-green-200 disabled:opacity-50"
                              title="Approve"
                            >
                              <CheckCircle size={18} />
                              <span>Approve</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                </p>
                <div className="flex space-x-2">
                  <button
                    onClick={() => fetchCompanies(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => fetchCompanies(pagination.page + 1)}
                    disabled={pagination.page >= pagination.pages}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail Modal */}
      {selectedCompany && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">{selectedCompany.companyName}</h3>
              <button onClick={() => { setSelectedCompany(null); setActionNotes(''); }} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium">{selectedCompany.email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <p className="font-medium">{selectedCompany.phoneNumber}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <StatusBadge status={selectedCompany.registrationStatus} />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Registered</p>
                  <p className="font-medium">{new Date(selectedCompany.createdAt).toLocaleDateString()}</p>
                </div>
                {selectedCompany.passkey && (
                  <div>
                    <p className="text-sm text-gray-500">Passkey</p>
                    <p className="font-medium font-mono">{selectedCompany.passkey}</p>
                  </div>
                )}
                {selectedCompany.fleetSize > 0 && (
                  <div>
                    <p className="text-sm text-gray-500">Fleet Size</p>
                    <p className="font-medium">{selectedCompany.fleetSize}</p>
                  </div>
                )}
              </div>

              {selectedCompany.servicesOffered?.length > 0 && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">Services</p>
                  <div className="flex flex-wrap gap-1">
                    {selectedCompany.servicesOffered.map((s, i) => (
                      <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full">{s}</span>
                    ))}
                  </div>
                </div>
              )}

              {selectedCompany.statusNotes && (
                <div>
                  <p className="text-sm text-gray-500">Previous Notes</p>
                  <p className="text-sm text-gray-700 bg-gray-50 p-2 rounded">{selectedCompany.statusNotes}</p>
                </div>
              )}

              {/* Action area */}
              <div className="border-t border-gray-200 pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                <textarea
                  value={actionNotes}
                  onChange={(e) => setActionNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                  rows={2}
                  placeholder="Add notes for this decision..."
                />
                <div className="flex space-x-3 mt-3">
                  {(selectedCompany.registrationStatus === 'pending' || selectedCompany.registrationStatus === 'suspended' || selectedCompany.registrationStatus === 'rejected') && (
                    <button
                      onClick={() => handleStatusUpdate(selectedCompany._id, 'approved')}
                      disabled={actionLoading}
                      className="flex-1 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50 text-sm"
                    >
                      {selectedCompany.registrationStatus === 'suspended' ? 'Reactivate' : 'Approve'}
                    </button>
                  )}
                  {selectedCompany.registrationStatus === 'pending' && (
                    <button
                      onClick={() => handleStatusUpdate(selectedCompany._id, 'rejected')}
                      disabled={actionLoading}
                      className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50 text-sm"
                    >
                      Reject
                    </button>
                  )}
                  {selectedCompany.registrationStatus === 'approved' && (
                    <button
                      onClick={() => handleStatusUpdate(selectedCompany._id, 'suspended')}
                      disabled={actionLoading}
                      className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50 text-sm"
                    >
                      Suspend
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyList;
